import ConfirmationDot from '@/components/ui/verification/confirmation-dot.tsx';
import { useDealContext } from '@/contexts/deal-provider';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import InlineTextInput from '../../ui/inline-text-input';

const CoRegistrantEmailEditor = observer(() => {
  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);
  const dealId = deal?.id;
  const dealShortId = deal?.short_id;
  const coRegistrant = deal?.co_registrant;
  const email = coRegistrant?.email || '';
  const coRegistrantId = coRegistrant?.id;
  const [emailValue, setEmailValue] = useState(email);

  const queryClient = useQueryClient();

  const updateCoRegistrantEmailMutation = useMutation({
    onMutate: async ({ coRegistrantEmail }) => {
      if (!dealId) return;

      dealStore.updateCoRegistrantEmail({
        id: dealId,
        email: coRegistrantEmail,
      });
    },
    mutationFn: async ({
      coRegistrantEmail,
    }: {
      coRegistrantEmail: NonNullable<Deal['co_registrant']>['email'];
    }) => {
      if (!coRegistrantEmail) return;

      const response = await pdkAxios
        .post(`/deals/${dealId}/co-registrant/${coRegistrantId}/email`, {
          email: coRegistrantEmail,
        })
        .catch((e) => {
          console.error('Error updating co-registrant email', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating co-registrant email', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const handleCoRegistrantEmailChange = useCallback(
    debounce((newCoRegistrantEmail: string) => {
      updateCoRegistrantEmailMutation.mutate({
        coRegistrantEmail: newCoRegistrantEmail,
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (emailValue === email) return;

    handleCoRegistrantEmailChange(emailValue);
  }, [emailValue]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailValue(e.target.value);
  }, []);

  useEffect(() => {
    setEmailValue(email);
  }, [email]);

  return (
    <div className="flex flex-row">
      <InlineTextInput
        key={`co-registrant-email-editor-${dealId}`}
        placeholder="Co-Registrant Email"
        value={emailValue}
        size="sm"
        className="w-full!"
        wrapperClassName="w-full!"
        onChange={onChange}
      />
      {dealId && (
        <ConfirmationDot
          targetId={dealId}
          targetType={ConfirmationTargetType.Deal}
          field={ConfirmationField.CoRegistrantEmail}
          value={observable.box(emailValue)}
        />
      )}
    </div>
  );
});

export default CoRegistrantEmailEditor;
